<template>
  <q-layout view="lHh LpR lFf" :class="{ 'theme-premium': isCompanyOne }">
    <q-header v-if="showSidebar && (isMobile || isImpersonating)" elevated class="text-white">
      <q-toolbar v-if="isMobile" class="bg-teal-9">
        <q-btn
          flat
          dense
          round
          icon="menu"
          aria-label="Menu"
          @click="toggleDrawer"
        />

        <q-toolbar-title class="text-weight-bold ellipsis" style="font-size: 1.1rem;">
          {{ route.meta.title || 'Digital Connecting' }}
        </q-toolbar-title>
      </q-toolbar>

      <!-- Impersonation Banner -->
      <q-banner v-if="isImpersonating" inline-actions class="bg-orange-8 text-white q-py-xs">
        <template v-slot:avatar>
          <q-icon name="admin_panel_settings" size="sm" />
        </template>
        Você está acessando como <strong>Super Admin</strong> na empresa: <strong>{{ currentCompany }}</strong>
        <template v-slot:action>
          <q-btn flat color="white" label="Encerrar Acesso" icon="logout" no-caps @click="logoutImpersonation" />
        </template>
      </q-banner>
    </q-header>

    <Sidebar v-if="showSidebar" :drawer="drawer" @toggleDrawer="toggleDrawer" />
    
    <q-page-container>
      <router-view/>
    </q-page-container>
  </q-layout>
</template>
<script>
import { ref, computed, provide, onMounted, onBeforeUnmount, watch } from "vue"
import { useQuasar, setCssVar } from "quasar"
import { useRoute, useRouter } from "vue-router"
import jwtDecode from "vue-jwt-decode"
import Sidebar from "@/components/SidebarComponent.vue"
import { notificationService } from "@/utils/notification"
import routes from "@/router/routes"
import socketManager from "@/config/socket"

export default {
  name: "App",
  components: { Sidebar },
  setup () {
    const $q = useQuasar()
    const route = useRoute()
    const router = useRouter()
    const deferredPrompt = ref(null);
    provide("deferredPrompt", deferredPrompt);

    const showSidebar = computed(() => route.path !== "/login")
    const isMobile = computed( () => $q.screen.lt.md )
    const drawer = ref( !isMobile.value )

    const handleKeydown = (e) => {
      if (e.ctrlKey && e.key === "ArrowUp") {
        e.preventDefault();
        showDebugMenu();
      }
    };

    const showDebugMenu = () => {
      $q.bottomSheet({
        message: `Depuração - Permissão: ${Notification.permission}`,
        dark: $q.dark.isActive,
        actions: [
          { label: 'Testar Som', icon: 'volume_up', id: 'sound' },
          { label: 'Testar Notificação', icon: 'notifications', id: 'notify' },
          { label: 'Solicitar Permissão', icon: 'lock_open', id: 'permission' }
        ]
      }).onOk(action => {
        if (action.id === 'sound') {
          const audio = new Audio("/notification.mp3");
          audio.play().catch(err => {
             console.error("Erro ao tocar som:", err);
             $q.notify({ message: "Erro ao tocar som: " + err, color: "red" });
          });
        } else if (action.id === 'notify') {
          notificationService.showBrowserNotification("Teste de Notificação", "As notificações estão funcionando corretamente!");
        } else if (action.id === 'permission') {
          notificationService.requestPermission();
        }
      });
    };

    const unlockAudio = () => {
      console.log("[App] Priming audio context on first interaction...");
      const audio = new Audio("/notification.mp3");
      audio.volume = 0; // Silent play
      audio.play().then(() => {
        window.removeEventListener("click", unlockAudio);
        window.removeEventListener("touchstart", unlockAudio);
        window.audioUnlocked = true;
        console.log("[App] Audio unlocked successfully.");
      }).catch(err => {
        console.warn("[App] Failed to prime audio:", err);
      });
    };

    const resolveAndApplyBranding = async () => {
      // 1. Aplica imediatamente as cores em cache se existirem para evitar "flicker"
      const cachedColors = localStorage.getItem('whitelabel_colors');
      if (cachedColors) {
        try {
          const colors = JSON.parse(cachedColors);
          if (colors.primary) setCssVar('primary', colors.primary);
          if (colors.secondary) setCssVar('secondary', colors.secondary);
        } catch (e) {
          console.debug('Cached colors failed to parse:', e);
        }
      }

      const cachedName = localStorage.getItem('whitelabel_name');
      if (cachedName) {
        document.title = cachedName;
      }

      const cachedLogo = localStorage.getItem('whitelabel_logo');
      if (cachedLogo) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = cachedLogo;
      }

      // 2. Resolve o domínio dinamicamente
      const hostname = window.location.hostname;
      if (hostname === "fallback.digitalconnecting.com.br") {
        return;
      }

      try {
        const companyData = await routes.CompanyController.resolveCompanyByDomain(hostname);
        if (companyData) {
          // Atualiza título da página
          const finalName = companyData.appName || companyData.name;
          document.title = finalName;
          localStorage.setItem('whitelabel_name', finalName);

          // Atualiza as cores personalizadas do Whitelabel
          if (companyData.colors) {
            const colors = companyData.colors;
            if (colors.primary) {
              setCssVar('primary', colors.primary);
            }
            if (colors.secondary) {
              setCssVar('secondary', colors.secondary);
            }
            localStorage.setItem('whitelabel_colors', JSON.stringify(colors));
          } else {
            localStorage.removeItem('whitelabel_colors');
          }

          // Atualiza o Logo personalizado
          if (companyData.logo) {
            localStorage.setItem('whitelabel_logo', companyData.logo);
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = companyData.logo;
          } else {
            localStorage.removeItem('whitelabel_logo');
          }

          // Atualiza a configuração do botão de cadastro
          if (companyData.registerConfig) {
            localStorage.setItem('whitelabel_registerConfig', JSON.stringify(companyData.registerConfig));
          } else {
            localStorage.removeItem('whitelabel_registerConfig');
          }
          
          // Dispara evento 'storage' local para avisar o Sidebar
          window.dispatchEvent(new Event('storage'));
        }
      } catch (error) {
        console.warn('Erro ao resolver domínio Whitelabel:', error);
      }
    };

    const handleUserNotification = (payload) => {
      let message = '';
      if (payload?.type === 'issue_assigned_to_you') {
        message = `Chamado #${payload.issueId} foi atribuído a você: "${payload.title}"`;
      } else if (payload?.type === 'issue_transferred_to_your_department') {
        message = `Chamado #${payload.issueId} foi transferido para o seu setor: "${payload.title}"`;
      } else {
        return;
      }

      $q.notify({
        color: 'teal',
        textColor: 'white',
        icon: 'notifications_active',
        position: 'top-right',
        message,
        timeout: 6000,
        actions: [{ label: 'Ver', color: 'white', handler: () => router.push('/issues') }],
      });
      notificationService.showBrowserNotification('Chamados', message);
    };

    let userNotificationSocket = null;
    let userNotificationEvent = null;
    let registeredNotificationUserId = null;

    // Called on mount AND on every route change: App.vue mounts once, before login
    // (login is a client-side SPA redirect, not a page reload), so onMounted alone
    // would only ever see the pre-login, token-less state.
    const setupUserNotifications = () => {
      const tokenString = localStorage.getItem('token');
      let token;
      try {
        token = JSON.parse(tokenString);
      } catch (error) {
        token = tokenString;
      }
      if (!token) {
        registeredNotificationUserId = null;
        return;
      }

      try {
        const { sub: userId } = jwtDecode.decode(token);
        if (!userId || userId === registeredNotificationUserId) return;

        if (userNotificationSocket && userNotificationEvent) {
          userNotificationSocket.off(userNotificationEvent, handleUserNotification);
        }

        userNotificationEvent = `user-${userId}-notification`;
        userNotificationSocket = socketManager.GetSocket();
        userNotificationSocket.on(userNotificationEvent, handleUserNotification);
        registeredNotificationUserId = userId;
      } catch (error) {
        console.debug('Não foi possível configurar notificações do usuário:', error);
      }
    };

    onMounted(async () => {
      // Resolve whitelabel dynamic branding
      await resolveAndApplyBranding();

      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode !== null) {
        $q.dark.set(savedDarkMode === 'true');
      } else {
        $q.dark.set('auto');
      }

      window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt.value = e;
      });

      window.addEventListener("keydown", handleKeydown);
      window.addEventListener("click", unlockAudio);
      window.addEventListener("touchstart", unlockAudio);

      setupUserNotifications();

      // Request notification permission
      await notificationService.requestPermission();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", handleKeydown);
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
      if (userNotificationSocket && userNotificationEvent) {
        userNotificationSocket.off(userNotificationEvent, handleUserNotification);
      }
    });

    watch(() => route.path, () => {
      if (isMobile.value) {
        drawer.value = false
      }
      setupUserNotifications();
    })

    function toggleDrawer () { drawer.value = !drawer.value }

    const user = computed(() => JSON.parse(localStorage.getItem("user") || "{}"));
    const isImpersonating = computed(() => user.value.isImpersonating);
    const currentCompany = computed(() => user.value.companyName || user.value.companyId);
    const isCompanyOne = computed(() => user.value.companyId == 1);

    async function logoutImpersonation() {
      try {
        $q.loading.show({ message: 'Retornando ao Super Admin...' });
        const response = await routes.AuthController.impersonate(1); // Return to Company 1
        
        localStorage.setItem("token",   response.token);
        localStorage.setItem("company", response.user.companyId);
        localStorage.setItem("user",    JSON.stringify(response.user));
        
        window.location.reload();
      } catch (error) {
        $q.notify({
          color: "red",
          message: "Erro ao retornar ao Super Admin."
        });
      } finally {
        $q.loading.hide();
      }
    }

    return { route, showSidebar, isMobile, drawer, toggleDrawer, isImpersonating, currentCompany, logoutImpersonation, isCompanyOne };
  }
}
</script>
<style scoped>
.mobile-menu-btn {
  position: absolute; /* or fixed */
  top: 10px;
  left: 10px;
  z-index: 5000;
}
</style>
<style>
body {
  overflow: hidden;
}
.custom-input .q-field__control,
.custom-input .q-field__control:before {
  border-radius: 12px !important;
}

.theme-premium.q-layout {
  --dashboard-bg: #0b0c0d;
  --card-bg: #1a1c1e;
  --q-primary: #62d2bc !important;
}

.theme-premium .bg-grey-10 {
  background-color: #0b0c0d !important;
}

.theme-premium .my-active-class {
  color: #62d2bc !important;
  border-right-color: #62d2bc !important;
}

.theme-premium .text-white {
  color: #ffffff !important;
}

.theme-premium .q-header.bg-teal-9 {
  background-color: #0b0c0d !important;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.theme-premium .q-btn.bg-primary {
  background-color: #62d2bc !important;
}
</style>
